import axios from "axios"
import Api from "@ciq/shared/api/api"
import Cookies from "js-cookie"

/**
 * Creates an axios instance configured for GeoAPI requests with authentication.
 *
 * @param {string} token - The API token for authentication
 * @returns {AxiosInstance} Configured axios instance
 */
const createApi = token => {
  return axios.create({
    baseURL: process.env.REACT_APP_GEOAPI_URL,
    headers: {
      "X-Api-Key": token,
      "Cache-Control": "no-cache",
    },
  })
}

/**
 * Singleton class that manages GeoAPI token fetching and caching.
 * Ensures that multiple concurrent requests for the same school's token
 * result in only one API call.
 */
class GeoApiTokenManager {
  constructor() {
    this.tokenPromises = new Map()
  }

  /**
   * Gets the singleton instance of GeoApiTokenManager.
   * @returns {GeoApiTokenManager} The singleton instance
   */
  static getInstance() {
    if (!GeoApiTokenManager.instance) {
      GeoApiTokenManager.instance = new GeoApiTokenManager()
    }
    return GeoApiTokenManager.instance
  }

  /**
   * Gets or fetches a token for the specified school.
   * Multiple concurrent calls for the same schoolId will share a single fetch operation.
   *
   * @param {string} schoolId - The unique identifier for the school
   * @returns {Promise<string>} A promise that resolves to the API token
   */
  async getToken(schoolId) {
    const key = `${schoolId}-geoapi-token`
    const storedToken = Cookies.get(key)

    if (storedToken != null) {
      return storedToken
    }

    // Check if we already have a pending request for this school
    if (this.tokenPromises.has(schoolId)) {
      return this.tokenPromises.get(schoolId)
    }

    // Create a new promise for fetching the token
    const tokenPromise = this.fetchToken(schoolId)
    this.tokenPromises.set(schoolId, tokenPromise)

    try {
      const token = await tokenPromise
      // Clean up the promise from the map once resolved
      this.tokenPromises.delete(schoolId)
      return token
    } catch (error) {
      // Clean up the promise from the map if it fails
      this.tokenPromises.delete(schoolId)
      throw error
    }
  }

  /**
   * Fetches a new token from the API and stores it in cookies.
   *
   * @param {string} schoolId - The unique identifier for the school
   * @returns {Promise<string>} A promise that resolves to the API token
   * @private
   */
  async fetchToken(schoolId) {
    const response = await Api.get(`schools/${schoolId}/map/token`)
    const { token, expires_in: expiresIn } = response.data
    const key = `${schoolId}-geoapi-token`
    Cookies.set(key, token, { secure: true, expires: expiresIn })
    return token
  }
}

/**
 * Creates and returns an axios instance configured for the GeoAPI with authentication.
 * Uses a singleton token manager to ensure multiple concurrent calls result in only one token fetch.
 *
 * @param {string} schoolId - The unique identifier for the school
 * @returns {AxiosInstance} Configured axios instance for GeoAPI requests
 */
export async function getGeoApi(schoolId) {
  const tokenManager = GeoApiTokenManager.getInstance()
  const token = await tokenManager.getToken(schoolId)
  return createApi(token)
}

/**
 * Constructs a file path for facility usage data.
 *
 * @param {Object} options - The options
 * @param {string} options.schoolId - The unique identifier for the school
 * @param {string|null|undefined} options.semesterId - The unique identifier for the semester
 * @param {string} options.table - The table name for the facility usage data
 * @returns {string} The constructed file path for the facility usage data file
 */
export function getPath({ schoolId, semesterId, table }) {
  if (typeof schoolId !== "string") {
    throw new Error(`Invalid schoolId type. Expected 'string' and got '${typeof schoolId}'`)
  }

  if (semesterId && typeof semesterId !== "string") {
    throw new Error(`Invalid semesterId type. Expected 'string' and got '${typeof semesterId}'`)
  }

  if (typeof table !== "string") {
    throw new Error(`Invalid table type. Expected 'string' and got '${typeof table}'`)
  }

  if (semesterId != null) {
    return `${schoolId}/facility_usage/${semesterId}_${table}.json.gz`
  }

  return `${schoolId}/facility_usage/${table}.json.gz`
}
